import { ShoppingCart, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import { CartLine } from './CartLine';
import { BundleCartLine } from './BundleCartLine';
import { MoneyValue } from './MoneyValue';
import { EmptyState } from './EmptyState';
import { QuoteDiscountRows } from './QuoteDiscountRows';
import { cartSelectors, selectTotal, useCartStore } from '@/stores/cart.store';
import { useVisitDateBlock } from '@/hooks/queries';
import { useCartQuote } from '@/hooks/useCartQuote';
import { useShallow } from 'zustand/react/shallow';
import { cn } from '@/lib/utils';

interface CartSidebarProps {
  onCheckout: () => void;
  className?: string;
  /** What "Lanjutkan" leads to, under the button (defaults to the Details step). */
  nextHint?: string;
}

export function CartSidebar({
  onCheckout,
  className,
  nextHint = 'Atur waktu & tambahan',
}: CartSidebarProps) {
  const { lines, bundleLines, updateQty, removeLine, updateBundleQty, removeBundle, clearCart } =
    useCartStore(
      useShallow((s) => ({
        lines: s.lines,
        bundleLines: s.bundleLines,
        updateQty: s.updateQty,
        removeLine: s.removeLine,
        updateBundleQty: s.updateBundleQty,
        removeBundle: s.removeBundle,
        clearCart: s.clearCart,
      })),
    );
  const subtotal = useCartStore(cartSelectors.subtotal);
  const savings = useCartStore(cartSelectors.couponSavings);
  const total = useCartStore(selectTotal);
  const itemCount = useCartStore(cartSelectors.itemCount);
  const isEmpty = lines.length === 0 && bundleLines.length === 0;
  // A blocked visit date cannot be sold — lock the proceed button.
  const visitDate = useCartStore((s) => s.visitDate);
  const dateBlocked = useVisitDateBlock(visitDate) !== null;
  // What checkout will charge, discounts included — so the cart shows the
  // promo before the cashier reaches payment. An FOC cart is free and has no
  // document yet on this page, so it is not quoted.
  const isFocCart = useCartStore((s) => s.mode === 'foc');
  const { quote, query, estimated } = useCartQuote({
    enabled: !isEmpty && !dateBlocked && !isFocCart,
    keepPrevious: true,
  });
  const shownSubtotal = quote?.subtotal ?? subtotal;
  const shownTotal = quote ? quote.total : total;

  return (
    <aside
      className={cn(
        'flex h-full w-full flex-col border-l border-border bg-card shadow-cart',
        className,
      )}
    >
      <header className="flex items-center justify-between border-b border-border px-5 py-4">
        <div className="flex items-center gap-2">
          <ShoppingCart className="h-5 w-5 text-primary" />
          <h2 className="font-display text-lg font-semibold">Keranjang</h2>
          {itemCount > 0 && (
            <span className="tnum rounded-full bg-primary px-2 py-0.5 text-xs font-bold text-primary-foreground">
              {itemCount}
            </span>
          )}
        </div>
        {!isEmpty && (
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-ink-400 hover:text-destructive"
            onClick={() => clearCart()}
          >
            <Trash2 className="h-4 w-4" /> Kosongkan
          </Button>
        )}
      </header>

      <ScrollArea className="flex-1 px-4 py-4">
        {isEmpty ? (
          <EmptyState
            icon={ShoppingCart}
            title="Keranjang kosong"
            description="Pilih produk atau paket dari katalog untuk memulai transaksi."
            className="border-0 bg-transparent py-16"
          />
        ) : (
          <div className="space-y-3">
            {bundleLines.map((line) => (
              <BundleCartLine
                key={line.key}
                line={line}
                onQty={updateBundleQty}
                onRemove={removeBundle}
              />
            ))}
            {lines.map((line) => (
              <CartLine key={line.key} line={line} onQty={updateQty} onRemove={removeLine} />
            ))}
          </div>
        )}
      </ScrollArea>

      <footer className="border-t border-border bg-cream-50/60 px-5 py-4">
        <dl className="space-y-1.5 text-sm">
          <div className="flex justify-between text-ink-600">
            <dt>Subtotal</dt>
            <dd>
              <MoneyValue value={shownSubtotal} />
            </dd>
          </div>
          <QuoteDiscountRows quote={quote} fallbackSavings={savings} />
        </dl>
        <Separator className="my-3" />
        <div className="flex items-end justify-between">
          <span className="text-sm font-semibold uppercase tracking-wide text-ink-600">Total</span>
          <span className="text-right">
            <MoneyValue value={shownTotal} emphasis className="text-3xl" />
            {query.isFetching ? (
              <span className="block text-[11px] font-normal text-ink-400">menghitung…</span>
            ) : quote && estimated ? (
              <span className="block text-[11px] font-normal text-ink-400">
                perkiraan · jam belum dipilih
              </span>
            ) : null}
          </span>
        </div>
        <Button
          size="lg"
          variant="gold"
          className="mt-4 h-16 w-full text-xl"
          disabled={isEmpty || dateBlocked}
          onClick={onCheckout}
        >
          Lanjutkan
          <ArrowRight className="h-6 w-6" />
        </Button>
        {dateBlocked ? (
          <p className="mt-1.5 text-center text-xs text-warning">
            Tanggal kunjungan ditutup — pilih tanggal lain untuk melanjutkan.
          </p>
        ) : (
          <p className="mt-1.5 text-center text-[11px] text-ink-400">
            {nextHint} · Tekan{' '}
            <kbd className="rounded border border-border bg-card px-1">F4</kbd>
          </p>
        )}
      </footer>
    </aside>
  );
}
